/**
 * @param { string } text
 * @return { string } capitalized camel-case text
 */
function capitalize(text: string): string {
  return text
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b([A-Z]+)([A-Z])([a-z])/, '$1 $2$3')
    .replace(/^./, (str) => str.toUpperCase());
}

/**
 * Formats the experiment or variable name.
 * @param name The name to format
 */
export function FormatName(name: string): string {
  return name ? capitalize(name.substring(name.indexOf('.') + 1)) : '';
}

/**
 * Formats the date to unix time
 * @param date The date to format
 */
export function FormatUnixTime(date: Date): number {
  return date ? Math.floor(date.getTime() / 1000) : 0;
}

/**
 * Converts the string to camelCase.
 * @param text the string to convert
 */
export function ToCamelCase(text: string): string {
  return text.replace(/(?:^\w|[A-Z]|\b\w|\s+)/g, (match, index) => {
    if (+match === 0) {
      return '';
    } else {
      return index === 0 ? match.toLowerCase() : match.toUpperCase();
    }
  });
}

/**
 * Checks if a string is camelCase.
 * @param str the string to check
 */
export function IsCamelCase(str: string): boolean {
  return ToCamelCase(str) === str;
}

/**
 * Checks if a string contains only letters or digits.
 * @param str the string to check
 */
export function IsAlphaNumeric(str: string): boolean {
  return /^[0-9a-zA-Z]+$/.test(str);
}

/**
 * Checks if a string contains only letters, digits or underscore.
 * @param str the string to check
 */
export function IsAlphaNumericWithUnderscore(str: string): boolean {
  return /^[0-9a-zA-Z_]+$/.test(str);
}

/**
 * Retrieves initials from email.
 * @param str
 */
export function GetInitials(str: string): string {
  return str
    .substring(0, str.lastIndexOf('@'))
    .replace('.', ' ')
    .replace(/[^a-zA-Z- ]/g, '')
    .match(/\b\w/g)!
    .join('')
    .toUpperCase();
}

/**
 * Formats error message recieved from the backend.
 */
export function FormatErrorMessage(e: any) {
  const arg = e?.response?.data?.arg;
  if (typeof arg === 'string' && arg) {
    return arg.toLowerCase().replace('\n', ', ');
  }
  return 'unknown error';
}

/**
 * Formats planner error message and only show the msg recieved from the backend.
 */
export function FormatErrorMessageV2(e: any) {
  try {
    return e.response.data.arg.split('msg=')[1].toLowerCase();
  } catch (e) {
    return 'unknown error';
  }
}

/**
 * Cleanse removes all 'undefined' from an object.
 *
 * @param { object } obj - object with possible undefined values
 * @return { object } same input object, cleaned from undefined values
 */
export function Cleanse(obj: any): any {
  Object.keys(obj).forEach(function (key) {
    // Get this value and its type
    const value = obj[key];
    const type = typeof value;
    if (type === 'object') {
      Cleanse(value);
    } else if (type === 'undefined') {
      // Undefined, remove it
      delete obj[key];
    }
  });
  return obj;
}

export function FormatBool(x: boolean): string {
  return x ? 'YES' : 'NO';
}

export function FormatPercentage(x: number): string {
  return (x * 100).toFixed(2).toString() + '%';
}
